import React, { useState } from 'react';
import { getLastPurchaseCostMap, savePurchase } from '../../data/purchases';
import { saveCatalog, LOW_STOCK_THRESHOLD } from '../../data/catalog';

export default function StockAlertsPanel({ catalogProducts, suppliers, purchases, setPurchases, setCatalogProducts, message, setMessage, onSwitchTab }) {
  const [assignSupplierId, setAssignSupplierId] = useState({});

  const formatMoney = (val) => Number(val || 0).toFixed(2) + ' €';

  const costMap = getLastPurchaseCostMap();
  
  // Identify low stock products
  const lowStockProducts = catalogProducts.filter(p => p.stock <= LOW_STOCK_THRESHOLD);

  // Group by supplier
  const grouped = {};
  const unassigned = [];

  lowStockProducts.forEach(p => {
    if (!p.supplierId) {
      unassigned.push(p);
    } else {
      if (!grouped[p.supplierId]) {
        grouped[p.supplierId] = [];
      }
      grouped[p.supplierId].push(p);
    }
  });

  const handleOrderAll = (supplierId, products) => {
    const items = products.map(p => {
      const suggestedQty = Math.max(10, LOW_STOCK_THRESHOLD - p.stock + 10);
      return {
        productId: p.id,
        quantity: suggestedQty,
        unitCost: costMap[p.id] || p.purchasePrice || 0
      };
    });

    const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unitCost), 0);
    const newPurchase = {
      id: Date.now().toString(),
      supplierId,
      items,
      status: 'draft',
      orderNumber: `BC-${Date.now()}`,
      totalAmount,
      createdAt: new Date().toISOString()
    };

    savePurchase(newPurchase);
    if (setPurchases) {
      setPurchases([...purchases, newPurchase]);
    }
    setMessage('Brouillon de commande créé avec succès.');
    setTimeout(() => setMessage(''), 3000);
    
    if (onSwitchTab) {
      onSwitchTab('orders');
    }
  };

  const handleAssignSupplier = (productId) => {
    const sId = assignSupplierId[productId];
    if (!sId) return;

    const updatedCatalog = catalogProducts.map(p => p.id === productId ? { ...p, supplierId: sId } : p);
    saveCatalog(updatedCatalog);
    setCatalogProducts(updatedCatalog);
    setMessage('Fournisseur assigné.');
    setTimeout(() => setMessage(''), 3000);
  };

  if (lowStockProducts.length === 0) {
    return (
      <div className="alert alert-success text-center py-5">
        <h4>✅ Tous les stocks sont bons !</h4>
        <p className="mb-0">Aucune alerte de stock bas détectée pour le moment.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="alert alert-warning">
        <strong>⚠️ {lowStockProducts.length} produits</strong> sont en rupture ou stock bas.
      </div>

      <div className="row">
        {Object.keys(grouped).map(supplierId => {
          const supplier = suppliers.find(s => s.id === supplierId);
          const products = grouped[supplierId];
          
          return (
            <div className="col-12 mb-4" key={supplierId}>
              <div className="card shadow-sm border-warning">
                <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    {supplier ? supplier.name : 'Fournisseur inconnu'}
                    <span className="badge bg-dark ms-2">{products.length} articles</span>
                  </h5>
                  <div>
                    {supplier?.phone && <a href={`tel:${supplier.phone}`} className="btn btn-sm btn-outline-dark me-2">📞 Appeler</a>}
                    {supplier?.email && <a href={`mailto:${supplier.email}`} className="btn btn-sm btn-outline-dark me-2">📧 Email</a>}
                    <button className="btn btn-sm btn-dark" onClick={() => handleOrderAll(supplierId, products)}>
                      🛒 Commander tout
                    </button>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Produit</th>
                          <th>Stock actuel</th>
                          <th>Stock Min</th>
                          <th>Dernier prix d'achat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map(p => (
                          <tr key={p.id}>
                            <td><strong>{p.name}</strong></td>
                            <td className="text-danger fw-bold">{p.stock}</td>
                            <td>{LOW_STOCK_THRESHOLD}</td>
                            <td>{formatMoney(costMap[p.id] || p.purchasePrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {unassigned.length > 0 && (
        <div className="card shadow-sm border-danger mt-4">
          <div className="card-header bg-danger text-white">
            <h5 className="mb-0">⚠️ Sans fournisseur ({unassigned.length} articles)</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Produit</th>
                    <th>Stock actuel</th>
                    <th>Assigner un fournisseur</th>
                  </tr>
                </thead>
                <tbody>
                  {unassigned.map(p => (
                    <tr key={p.id}>
                      <td className="align-middle"><strong>{p.name}</strong></td>
                      <td className="align-middle text-danger fw-bold">{p.stock}</td>
                      <td>
                        <div className="input-group input-group-sm w-50">
                          <select className="form-select" 
                            value={assignSupplierId[p.id] || ''} 
                            onChange={(e) => setAssignSupplierId({...assignSupplierId, [p.id]: e.target.value})}>
                            <option value="">Choisir...</option>
                            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                          <button className="btn btn-outline-secondary" onClick={() => handleAssignSupplier(p.id)}>Assigner</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
